import React, { useState, useRef, useEffect } from "react";
import styles from "./Tabs.module.scss";
import CaretRight from "../../assets/svg/icons/common/CaretRight";
import useDomainStore from "../../stores/domain";

const SCROLL_AMOUNT = 150;

const getBadgeStyle = (badgeStyle) => {
  switch (badgeStyle) {
    case "normal":
      return styles.badgeNormal;
    case "success":
      return styles.badgeSuccess;
    case "danger":
      return styles.badgeDanger;
    case "info":
      return styles.badgeInfo;
    default:
      return "";
  }
};

const Tabs = ({ tabs, onSelect, active_index = 0, isMobile = false }) => {
  const { isKurvDomain } = useDomainStore();
  const tabRefs = useRef([]);
  const tabsContainerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(active_index);
  const [activeBorderStyle, setActiveBorderStyle] = useState({
    left: "0px",
    width: "0px",
  });
  const [arrowsNeeded, setArrowsNeeded] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const handleTabClick = (index) => {
    setActiveIndex(index);
    if (onSelect) onSelect(index);
  };

  const shouldShowLeftArrow = () => {
    return tabsContainerRef.current.scrollLeft > 16;
  };

  const shouldShowRightArrow = () => {
    const { scrollLeft, scrollWidth, clientWidth } = tabsContainerRef.current;
    return scrollLeft < scrollWidth - clientWidth - 16;
  };

  const handleRightArrowClick = () => {
    tabsContainerRef.current.scrollLeft += SCROLL_AMOUNT;
    requestAnimationFrame(handleArrowCheck);
  };

  const handleLeftArrowClick = () => {
    tabsContainerRef.current.scrollLeft -= SCROLL_AMOUNT;
    requestAnimationFrame(handleArrowCheck);
  };

  const areArrowsNeeded = () => {
    const { scrollWidth, clientWidth } = tabsContainerRef.current;
    return scrollWidth > clientWidth;
  };

  const handleArrowCheck = () => {
    setArrowsNeeded(areArrowsNeeded());
    setShowLeftArrow(shouldShowLeftArrow());
    setShowRightArrow(shouldShowRightArrow());
  };

  useEffect(() => {
    if (!tabRefs.current[activeIndex]) {
      return;
    }

    const offsetLeft = tabRefs.current[activeIndex].offsetLeft;
    const offsetWidth = tabRefs.current[activeIndex].offsetWidth;

    setActiveBorderStyle({
      left: `${offsetLeft}px`,
      width: `${offsetWidth}px`,
    });

    tabRefs.current[activeIndex].scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });

    handleArrowCheck();
  }, [activeIndex, tabs]);

  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(handleArrowCheck);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (!tabsContainerRef.current) {
      return;
    }

    // for some reason if there are more than 5 tabs the tabsContainer is
    // scrolled to the right, investigate later
    tabsContainerRef.current.scrollLeft = 0;

    const handleScroll = () => {
      requestAnimationFrame(handleArrowCheck);
    };

    tabsContainerRef.current.addEventListener("scroll", handleScroll);

    return () => {
      if (tabsContainerRef.current) {
        tabsContainerRef.current.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  return (
    <div className={`${styles.tabsContainer} ${isKurvDomain ? styles.kurv_theme : ""} ${isMobile ? styles.mobile : ""}`}>
      {arrowsNeeded && (
        <div className={styles.tabsArrowContainer}>
          <div
            className={styles.leftArrow}
            style={{ display: showLeftArrow ? "block" : "none" }}
            onClick={handleLeftArrowClick}
          >
            <CaretRight />
          </div>
          <div
            className={styles.rightArrow}
            style={{ display: showRightArrow ? "block" : "none" }}
            onClick={handleRightArrowClick}
          >
            <CaretRight />
          </div>
        </div>
      )}

      <div className={`${styles.tabs}`} ref={tabsContainerRef}>
        {tabs
          // .filter((t) => !t?.hidden)
          .map((tab, index) => (
            <button
              key={`tab-${index}`}
              className={`${styles.tab} ${
                activeIndex === index ? styles.active : ""
              } ${tab?.hidden ? styles.hidden : ""} ${tab?.mobileOnly ? styles.mobileOnly : ""}`}
              onClick={() => handleTabClick(index)}
              ref={(el) => (tabRefs.current[index] = el)}
            >
              {tab.label}
              {tab.badge && (
                <span
                  className={`${styles.badge} ${getBadgeStyle(tab?.badgeStyle || "")}`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        <div 
          className={styles.activeBorder} 
          style={{
            ...activeBorderStyle,
            backgroundColor: isKurvDomain ? "#22A276" : "#4c70ef"
          }} 
        />
      </div>
      <div className={styles.tabContent}>
        {tabs[activeIndex]?.content || <p>No content available</p>}
      </div>
    </div>
  );
};

export default Tabs;
